import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { LangProvider } from "./context/LangContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import FilamentsPage from "./pages/FilamentsPage";
import AccessoriesPage from "./pages/AccessoriesPage";
import CalculatorPage from "./pages/CalculatorPage";
import SalesPage from "./pages/SalesPage";
import PurchasesPage from "./pages/PurchasesPage";
import SettingsPage from "./pages/SettingsPage";
import BannersPage from "./pages/BannersPage";
import AdminPanelPage from "./pages/AdminPanelPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import ProfilePage from "./pages/ProfilePage";
import BugReportPage from "./pages/BugReportPage";
import PublicListinoPage from "./pages/PublicListinoPage";
import HomeShopPage from "./pages/HomeShopPage";
import NotFoundShopPage from "./pages/NotFoundShopPage";
import PublicProductDetailPage from "./pages/PublicProductDetailPage";
import { PageTracker } from "./components/PageTracker";
import LandingPage from "./pages/LandingPage";
import GuidePage from "./pages/GuidePage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import ClientsPage from "./pages/ClientsPage";
import QuotesPage from "./pages/QuotesPage";
import DemoCalculatorPage from "./pages/DemoCalculatorPage";
import CricutPage from "./pages/CricutPage";
import CricutCalculatorPage from "./pages/CricutCalculatorPage";
import Print3DLayout from "./components/Print3DLayout";
import NewsListPage from "./pages/NewsListPage";
import NewsDetailPage from "./pages/NewsDetailPage";
import { CookieBanner } from "./components/CookieBanner";
import { PublicScripts } from "./components/PublicScripts";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground">...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground">...</div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function ShopAdminGate({ children }) {
  // Nel dominio shop, /admin richiede login. Se non loggato mostra la pagina di login
  // (in questo modo il proprietario puo' accedere senza dover conoscere /login).
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground">...</div>
      </div>
    );
  }
  if (!user) return <LoginPage />;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  // Se il dominio è shop/listino, mostra direttamente lo Shop pubblico (no login).
  // Il flag ?__shop=1 forza la modalita' shop anche dal preview per testing.
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const forceShop = typeof window !== 'undefined' && window.location.search.includes('__shop=1');
  const isShopDomain = host === 'shop.artestramas3d.it' || host === 'listino.artestramas3d.it' || forceShop;

  if (isShopDomain) {
    return (
      <>
        <PageTracker />
        <Routes>
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/news" element={<NewsListPage />} />
          <Route path="/news/:slug" element={<NewsDetailPage />} />
          <Route path="/shop/prodotto/:slug" element={<PublicProductDetailPage />} />
          <Route path="/prodotto/:slug" element={<PublicProductDetailPage />} />
          <Route path="/listino" element={<PublicListinoPage />} />
          {/* /admin: 404 se non loggato, admin panel se autorizzato */}
          <Route path="/admin" element={<ShopAdminGate><AdminPanelPage /></ShopAdminGate>} />
          {/* /login accessibile per il proprietario ma non linkato */}
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/" element={<HomeShopPage />} />
          {/* Qualsiasi altra rotta (calculator, filaments, sales, ecc.) -> 404 shop */}
          <Route path="*" element={<NotFoundShopPage />} />
        </Routes>
      </>
    );
  }

  return (
    <>
      <PageTracker />
      <Routes>
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      {/* Stampa 3D: pagina unificata con 4 tab (Preventivi / Calcolatore / Filamenti / Accessori) */}
      <Route path="/print3d" element={<ProtectedRoute><Print3DLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/print3d/quotes" replace />} />
        <Route path="quotes" element={<QuotesPage />} />
        <Route path="calculator" element={<CalculatorPage />} />
        <Route path="filaments" element={<FilamentsPage />} />
        <Route path="accessories" element={<AccessoriesPage />} />
      </Route>
      {/* Vecchie URL → redirect ai nuovi tab (retrocompatibilita') */}
      <Route path="/filaments" element={<Navigate to="/print3d/filaments" replace />} />
      <Route path="/accessories" element={<Navigate to="/print3d/accessories" replace />} />
      <Route path="/calculator" element={<Navigate to="/print3d/calculator" replace />} />
      <Route path="/quotes" element={<Navigate to="/print3d/quotes" replace />} />
      <Route path="/sales" element={<ProtectedRoute><SalesPage /></ProtectedRoute>} />
      <Route path="/cricut" element={<ProtectedRoute><CricutPage /></ProtectedRoute>} />
      <Route path="/cricut/calculator" element={<ProtectedRoute><CricutCalculatorPage /></ProtectedRoute>} />
      <Route path="/cricut/calculator/:pid" element={<ProtectedRoute><CricutCalculatorPage /></ProtectedRoute>} />
      <Route path="/purchases" element={<ProtectedRoute><PurchasesPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/banners" element={<ProtectedRoute><BannersPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPanelPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/bug-report" element={<ProtectedRoute><BugReportPage /></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute><ClientsPage /></ProtectedRoute>} />
      {/* Public pages - no auth */}
      <Route path="/listino" element={<PublicListinoPage />} />
      <Route path="/shop/prodotto/:slug" element={<PublicProductDetailPage />} />
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/demo" element={<DemoCalculatorPage />} />
      <Route path="/guide" element={<GuidePage />} />
      <Route path="/news" element={<NewsListPage />} />
      <Route path="/news/:slug" element={<NewsDetailPage />} />
      <Route path="/cookie-policy" element={<CookiePolicyPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <LangProvider>
          <AuthProvider>
            <BrowserRouter>
              <AppRoutes />
              <CookieBanner />
              <PublicScripts />
              <Toaster position="top-right" richColors />
            </BrowserRouter>
          </AuthProvider>
        </LangProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
